import React, { useState, useEffect } from "react";
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { baseUrl, requestID, config } from "../../../common/utils";
import { formattedDateTime } from "../../../common/utils_t";

interface ElectionForm {
  name: string;
  election_id: string;
  no_of_aspirants: string;
  no_of_positions: string;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
}

const ElectionSetup = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [elections, setElections] = useState<any[]>([]);
  const [enabled, setEnabled] = useState(true);
  const history = useHistory();

  const [form, setForm] = useState<ElectionForm>({
    name: "",
    election_id: "",
    no_of_aspirants: "",
    no_of_positions: "",
    start_date: dayjs(),
    end_date: dayjs().add(1, 'day'),
  });

  useEffect(() => {
    fetchElections();
  }, []);

  const fetchElections = async () => {
    const formData = new FormData();
    formData.append("requestID", requestID());

    try {
      const res = await axios.post(baseUrl() + "elections/getAllElections", formData, config);
      if (res.data?.data) {
        setElections(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching elections:", err);
      alert("Failed to fetch elections");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDateChange = (field: keyof ElectionForm) => (value: Dayjs | null) => {
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      
      // Ensure end date is after start date
      if (field === 'start_date' && value && newForm.end_date && value.isAfter(newForm.end_date)) {
        newForm.end_date = value.add(1, 'day');
      }
      
      return newForm;
    });
  };

  const handleAddElection = async () => {
    if (!form.start_date || !form.end_date) {
      alert("Please select both start and end dates");
      return;
    }

    if (form.end_date.isBefore(form.start_date)) {
      alert("End date must be after start date");
      return;
    }

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("name", form.name);
    formData.append("election_id", form.election_id);
    formData.append("no_of_aspirants", form.no_of_aspirants);
    formData.append("no_of_positions", form.no_of_positions);
    formData.append("start_date", form.start_date.toISOString());
    formData.append("end_date", form.end_date.toISOString());
    formData.append("status", enabled ? "1" : "0");

    try {
      await axios.post(baseUrl() + "elections/addElection", formData, config);
      setForm({
        name: "",
        election_id: "",
        no_of_aspirants: "",
        no_of_positions: "",
        start_date: dayjs(),
        end_date: dayjs().add(1, 'day'),
      });
      setIsModalOpen(false);
      setEnabled(true);
      fetchElections();
    } catch (err) {
      console.error(err);
      alert("Failed to add election");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this election?")) return;

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("election_id", id);

    try {
      await axios.post(baseUrl() + "elections/deleteElection", formData, config);
      fetchElections();
    } catch (err) {
      console.error("Error deleting election:", err);
      alert("Failed to delete election");
    }
  };

  const onManage = (id: string) => {
    history.push(`/folder/manage-election/${id}`, { state: { dataId: id } });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Manage Election</IonTitle>
          <IonButtons slot="end">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setIsModalOpen(true)}
            >
              Add Election
            </Button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Dialog
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add New Election</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Fill in the details for the new election
              </DialogContentText>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    margin="dense"
                    name="name"
                    label="Election Name"
                    fullWidth
                    variant="outlined"
                    value={form.name}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    name="no_of_positions"
                    label="Number of Positions"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={form.no_of_positions}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    name="no_of_aspirants"
                    label="Number of Aspirants"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={form.no_of_aspirants}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Start Date"
                    value={form.start_date}
                    onChange={handleDateChange('start_date')}
                    minDate={dayjs()}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth margin="dense" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="End Date"
                    value={form.end_date}
                    onChange={handleDateChange('end_date')}
                    minDate={form.start_date?.add(1, 'minute')}
                    disabled={!form.start_date}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth margin="dense" />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsModalOpen(false)} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleAddElection}
                color="primary"
                variant="contained"
              >
                Add Election
              </Button>
            </DialogActions>
          </Dialog>
        </LocalizationProvider>

        <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="elections table">
            <TableHead>
              <TableRow>
                <TableCell><strong>S/N</strong></TableCell>
                <TableCell><strong>Election Name</strong></TableCell>
                <TableCell><strong>Start Date</strong></TableCell>
                <TableCell><strong>End Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {elections.length > 0 ? (
                elections.map((election, index) => (
                  <TableRow key={election.election_id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{election.name}</TableCell>
                    <TableCell>{formattedDateTime(election.start_data)}</TableCell>
                    <TableCell>{formattedDateTime(election.end_data)}</TableCell>
                    <TableCell>{election.status === "1" ? "Active" : "Inactive"}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(election)}
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(election.election_id)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => onManage(election.election_id)}
                          sx={{ ml: 1 }}
                        >
                          Manage
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No elections found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </IonContent>
    </IonPage>
  );
};

export default ElectionSetup;